import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileAlt, faPrint, faArrowsRotate, faCircleCheck,
  faTriangleExclamation, faPersonWalkingLuggage, faPersonWalking,
  faBed, faMoneyBill, faWrench, faBroom,
} from "@fortawesome/free-solid-svg-icons";
import DashboardLayout from "../layout/DashboardLayout";
import "./ShiftHandoverPage.css";

const API = import.meta.env.VITE_API_URL ?? "http://localhost:5050";

interface HandoverData {
  date: string;
  generatedAt: string;
  arrivals: any[];
  departures: any[];
  inHouse: any[];
  pendingPayments: any[];
  openMaintenance: any[];
  housekeeping: { dirty: number; cleaning: number; clean: number; inspected: number };
  notes: string;
}

function today() {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,"0")}-${String(n.getDate()).padStart(2,"0")}`;
}

function fmt(d?: string | null) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function nowStr() {
  return new Date().toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function ShiftHandoverPage() {
  const [report, setReport]   = useState<HandoverData | null>(null);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes]     = useState("");
  const [notesSaving, setNotesSaving] = useState(false);
  const [toast, setToast]     = useState("");
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showToast = (msg: string) => { setToast(msg); setTimeout(() => setToast(""), 3000); };

  // Load today's notes from DB on mount
  useEffect(() => {
    axios.get(`${API}/api/dashboard/shift-notes/${today()}`)
      .then(res => setNotes(res.data?.notes || ""))
      .catch(() => {});
  }, []);

  const saveNotes = useCallback(async (val: string) => {
    setNotesSaving(true);
    try {
      await axios.put(`${API}/api/dashboard/shift-notes/${today()}`, { notes: val });
    } catch {
      showToast("Failed to save notes");
    } finally {
      setNotesSaving(false);
    }
  }, []);

  const handleNotesChange = (val: string) => {
    setNotes(val);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => saveNotes(val), 1200);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const td = today();
      const [notifRes, bookingsRes, hkRes] = await Promise.all([
        axios.get(`${API}/api/dashboard/notifications`),
        axios.get(`${API}/api/bookings`).catch(() => ({ data: [] })),
        axios.get(`${API}/api/housekeeping`).catch(() => ({ data: [] })),
      ]);

      const notif = notifRes.data;
      const allBookings: any[] = Array.isArray(bookingsRes.data) ? bookingsRes.data : [];
      const rooms: any[] = Array.isArray(hkRes.data) ? hkRes.data : [];

      // In-house (checked-in, not departing today)
      const departingIds = new Set((notif.todayDepartures || []).map((b: any) => b.bookingId || b.id));
      const in_house = allBookings.filter(b => {
        const id = b.bookingid || b.bookingId || "";
        if (departingIds.has(id)) return false;
        if (b.status === "cancelled" || b.status === "deleted") return false;
        const cin = b.checkin || "";
        const cout = b.checkout || "";
        return cin <= td && cout > td;
      });

      // Pending payments: bookings where total > 0 and not "paid"
      const pending = allBookings.filter(b =>
        b.status !== "cancelled" && b.status !== "deleted" &&
        (b.paymentstatus === "pending" || !b.paymentstatus) &&
        ((b.total || 0) > 0) &&
        (b.checkin <= td)
      ).slice(0, 10);

      // Housekeeping counts
      const hkCounts = rooms.reduce((acc: any, r: any) => {
        acc[r.status] = (acc[r.status] || 0) + 1;
        return acc;
      }, {});

      // Maintenance (try to load, silent fail if table not ready)
      let maintenance: any[] = [];
      try {
        const mtRes = await axios.get(`${API}/api/maintenance?status=open`);
        maintenance = Array.isArray(mtRes.data) ? mtRes.data.slice(0, 10) : [];
      } catch { /* table may not exist yet */ }

      setReport({
        date: td,
        generatedAt: nowStr(),
        arrivals: (notif.todayArrivals || []).slice(0, 20),
        departures: (notif.todayDepartures || []).slice(0, 20),
        inHouse: in_house.slice(0, 20),
        pendingPayments: pending,
        openMaintenance: maintenance,
        housekeeping: {
          dirty: hkCounts.dirty || 0,
          cleaning: hkCounts.cleaning || 0,
          clean: hkCounts.clean || 0,
          inspected: hkCounts.inspected || 0,
        },
        notes: "",
      });
    } catch {
      showToast("Failed to generate shift handover report");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, []);

  const print = () => window.print();

  if (loading) return (
    <DashboardLayout>
      <div className="sh-page"><div className="sh-loading">Generating shift handover report…</div></div>
    </DashboardLayout>
  );

  return (
    <DashboardLayout>
      <div className="sh-page">
        {/* Screen-only header */}
        <div className="sh-header no-print">
          <div>
            <h1 className="sh-title"><FontAwesomeIcon icon={faFileAlt} /> Shift Handover Report</h1>
            <p className="sh-subtitle">Auto-generated · {report?.date} · {report?.generatedAt}</p>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button className="sh-btn-refresh" onClick={load}>
              <FontAwesomeIcon icon={faArrowsRotate} /> Regenerate
            </button>
            <button className="sh-btn-print" onClick={print}>
              <FontAwesomeIcon icon={faPrint} /> Print
            </button>
          </div>
        </div>

        {/* ── PRINT BODY ── */}
        <div className="sh-report" id="sh-print-area">
          {/* Print header (hidden on screen) */}
          <div className="sh-print-header print-only">
            <div className="sh-print-hotel">SHIFT HANDOVER REPORT</div>
            <div className="sh-print-meta">Date: {report?.date} &nbsp;|&nbsp; Generated: {report?.generatedAt}</div>
            <hr className="sh-print-divider" />
          </div>

          {/* KPI row */}
          <div className="sh-kpi-row">
            <div className="sh-kpi-card sh-kpi-green">
              <FontAwesomeIcon icon={faPersonWalkingLuggage} />
              <div className="sh-kpi-val">{report?.arrivals.length || 0}</div>
              <div className="sh-kpi-label">Arrivals Today</div>
            </div>
            <div className="sh-kpi-card sh-kpi-blue">
              <FontAwesomeIcon icon={faPersonWalking} />
              <div className="sh-kpi-val">{report?.departures.length || 0}</div>
              <div className="sh-kpi-label">Departures Today</div>
            </div>
            <div className="sh-kpi-card sh-kpi-gold">
              <FontAwesomeIcon icon={faBed} />
              <div className="sh-kpi-val">{report?.inHouse.length || 0}</div>
              <div className="sh-kpi-label">In House</div>
            </div>
            <div className="sh-kpi-card sh-kpi-red">
              <FontAwesomeIcon icon={faMoneyBill} />
              <div className="sh-kpi-val">{report?.pendingPayments.length || 0}</div>
              <div className="sh-kpi-label">Pending Payments</div>
            </div>
          </div>

          {/* Housekeeping summary */}
          <div className="sh-section">
            <h2 className="sh-section-title"><FontAwesomeIcon icon={faBroom} /> Housekeeping Status</h2>
            <div className="sh-hk-row">
              <div className="sh-hk-cell sh-hk-dirty">
                <div className="sh-hk-num">{report?.housekeeping.dirty}</div>
                <div className="sh-hk-lbl">Dirty</div>
              </div>
              <div className="sh-hk-cell sh-hk-cleaning">
                <div className="sh-hk-num">{report?.housekeeping.cleaning}</div>
                <div className="sh-hk-lbl">Cleaning</div>
              </div>
              <div className="sh-hk-cell sh-hk-clean">
                <div className="sh-hk-num">{report?.housekeeping.clean}</div>
                <div className="sh-hk-lbl">Clean</div>
              </div>
              <div className="sh-hk-cell sh-hk-inspected">
                <div className="sh-hk-num">{report?.housekeeping.inspected}</div>
                <div className="sh-hk-lbl">Inspected</div>
              </div>
            </div>
          </div>

          {/* Arrivals */}
          <div className="sh-section">
            <h2 className="sh-section-title sh-green">
              <FontAwesomeIcon icon={faPersonWalkingLuggage} /> Today's Arrivals ({report?.arrivals.length})
            </h2>
            {report!.arrivals.length === 0 ? (
              <p className="sh-none">No arrivals today</p>
            ) : (
              <table className="sh-table">
                <thead>
                  <tr><th>Booking ID</th><th>Guest</th><th>Room</th><th>Nights</th><th>Status</th></tr>
                </thead>
                <tbody>
                  {report!.arrivals.map((b: any, i: number) => (
                    <tr key={i}>
                      <td>{b.bookingId || b.id}</td>
                      <td>{b.guestName || b.guestname}</td>
                      <td>{b.roomNumber}</td>
                      <td>{b.nights}</td>
                      <td>{b.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Departures */}
          <div className="sh-section">
            <h2 className="sh-section-title sh-blue">
              <FontAwesomeIcon icon={faPersonWalking} /> Today's Departures ({report?.departures.length})
            </h2>
            {report!.departures.length === 0 ? (
              <p className="sh-none">No departures today</p>
            ) : (
              <table className="sh-table">
                <thead>
                  <tr><th>Booking ID</th><th>Guest</th><th>Room</th><th>Nights</th><th>Status</th></tr>
                </thead>
                <tbody>
                  {report!.departures.map((b: any, i: number) => (
                    <tr key={i}>
                      <td>{b.bookingId || b.id}</td>
                      <td>{b.guestName || b.guestname}</td>
                      <td>{b.roomNumber}</td>
                      <td>{b.nights}</td>
                      <td>{b.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Pending Payments */}
          {(report?.pendingPayments.length || 0) > 0 && (
            <div className="sh-section">
              <h2 className="sh-section-title sh-red">
                <FontAwesomeIcon icon={faTriangleExclamation} /> Pending Payments ({report!.pendingPayments.length})
              </h2>
              <table className="sh-table">
                <thead>
                  <tr><th>Booking ID</th><th>Guest</th><th>Check-in</th><th>Total</th><th>Payment Status</th></tr>
                </thead>
                <tbody>
                  {report!.pendingPayments.map((b: any, i: number) => (
                    <tr key={i} className="sh-row-alert">
                      <td>{b.bookingid || b.bookingId}</td>
                      <td>{b.guestname || b.guestName}</td>
                      <td>{fmt(b.checkin)}</td>
                      <td>{b.total?.toLocaleString()}</td>
                      <td>{b.paymentstatus || "pending"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Open Maintenance */}
          {(report?.openMaintenance.length || 0) > 0 && (
            <div className="sh-section">
              <h2 className="sh-section-title sh-orange">
                <FontAwesomeIcon icon={faWrench} /> Open Maintenance ({report!.openMaintenance.length})
              </h2>
              <table className="sh-table">
                <thead>
                  <tr><th>#</th><th>Title</th><th>Location</th><th>Priority</th><th>Assigned</th></tr>
                </thead>
                <tbody>
                  {report!.openMaintenance.map((t: any, i: number) => (
                    <tr key={i}>
                      <td>{t.id}</td>
                      <td>{t.title}</td>
                      <td>{t.location || "—"}</td>
                      <td>{t.priority}</td>
                      <td>{t.assigned_to || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Handover Notes */}
          <div className="sh-section sh-notes-section">
            <h2 className="sh-section-title">
              <FontAwesomeIcon icon={faCircleCheck} /> Handover Notes
              {notesSaving && <span style={{ fontSize: 11, color: "rgba(242,235,217,0.4)", marginLeft: 10, fontWeight: 400 }}>Saving…</span>}
              {!notesSaving && notes && <span style={{ fontSize: 11, color: "#34d399", marginLeft: 10, fontWeight: 400 }}>✓ Saved</span>}
            </h2>
            <textarea
              className="sh-notes-input no-print"
              rows={5}
              placeholder="Add handover notes, special requests, or important instructions for the next shift…"
              value={notes}
              onChange={e => handleNotesChange(e.target.value)}
            />
            {notes && <div className="sh-notes-print print-only">{notes}</div>}
            {!notes && <div className="sh-notes-print print-only sh-notes-empty">No notes added for this shift.</div>}
          </div>

          {/* Print footer */}
          <div className="sh-print-footer print-only">
            <hr className="sh-print-divider" />
            <p>Received by: _________________________ &nbsp;&nbsp; Signature: _________________________ &nbsp;&nbsp; Time: _________</p>
          </div>
        </div>

        {toast && <div className="sh-toast">{toast}</div>}
      </div>
    </DashboardLayout>
  );
}
